import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { strideThreatService } from '@/services/strideThreatService';
import { queryKeys } from '@/lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Trash2, RefreshCw, Shield, Grid3X3, List, X } from 'lucide-react';
import { toast } from 'sonner';
import type {
  StrideThreat,
  StrideCategory,
  StrideStatus,
} from '@/types/stride';
import {
  STRIDE_LABELS,
  STRIDE_COLORS,
  STATUS_CONFIG,
} from '@/types/stride';

const ALL_CATEGORIES: StrideCategory[] = ['S', 'T', 'R', 'I', 'D', 'E'];

export default function StrideTab() {
  const { projectId } = useProject();
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<StrideCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StrideStatus | 'all'>('all');
  const [collapsedEntities, setCollapsedEntities] = useState<Set<string>>(new Set());
  const [clearing, setClearing] = useState(false);
  const [viewMode, setViewMode] = useState<'heatmap' | 'list'>('heatmap');
  const [entityFilter, setEntityFilter] = useState<string | 'all'>('all');

  const { data: threats = [], isLoading: loading, refetch } = useQuery({
    queryKey: queryKeys.strideThreats.all(projectId!),
    queryFn: () => strideThreatService.list(projectId!),
    enabled: !!projectId && !isNaN(projectId),
  });

  // ── Computed stats ──────────────────────────────────────────────────────

  const categoryCounts = useMemo(() => {
    const counts: Record<StrideCategory, Record<StrideStatus | 'total', number>> = {} as never;
    for (const c of ALL_CATEGORIES) {
      counts[c] = { total: 0, open: 0, partial: 0, mitigated: 0, accepted: 0, na: 0 };
    }
    for (const t of threats) {
      const c = t.stride_category as StrideCategory;
      if (counts[c]) {
        counts[c].total++;
        counts[c][t.status]++;
      }
    }
    return counts;
  }, [threats]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: threats.length, open: 0, partial: 0, mitigated: 0, accepted: 0, na: 0 };
    for (const t of threats) {
      counts[t.status] = (counts[t.status] || 0) + 1;
    }
    return counts;
  }, [threats]);

  // ── Filtering ───────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return threats.filter((t) => {
      if (categoryFilter !== 'all' && t.stride_category !== categoryFilter) return false;
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (entityFilter !== 'all' && t.entity_name !== entityFilter) return false;
      return true;
    });
  }, [threats, categoryFilter, statusFilter, entityFilter]);

  // Group by entity
  const grouped = useMemo(() => {
    const map = new Map<string, StrideThreat[]>();
    for (const t of filtered) {
      const key = t.entity_name;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return map;
  }, [filtered]);

  const toggleEntity = (name: string) => {
    setCollapsedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const handleClearAll = async () => {
    if (!projectId) return;
    setClearing(true);
    try {
      await strideThreatService.clearAll(projectId);
      queryClient.setQueryData(queryKeys.strideThreats.all(projectId), []);
      toast.success('All STRIDE threats cleared');
    } catch {
      toast.error('Failed to clear STRIDE threats');
    } finally {
      setClearing(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await strideThreatService.remove(id);
      if (projectId) queryClient.setQueryData<StrideThreat[]>(queryKeys.strideThreats.all(projectId), (prev) => prev?.filter((t) => t.id !== id));
    } catch {
      toast.error('Failed to delete threat');
    }
  };

  // ── Heatmap data ──────────────────────────────────────────────────────

  const heatmapData = useMemo(() => {
    const entityMap = new Map<string, Record<StrideCategory, number>>();
    for (const t of threats) {
      if (!entityMap.has(t.entity_name)) {
        entityMap.set(t.entity_name, { S: 0, T: 0, R: 0, I: 0, D: 0, E: 0 });
      }
      const row = entityMap.get(t.entity_name)!;
      const cat = t.stride_category as StrideCategory;
      if (cat in row) row[cat]++;
    }
    // Sort by total count descending
    return Array.from(entityMap.entries())
      .map(([name, counts]) => ({
        name,
        counts,
        total: Object.values(counts).reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [threats]);

  const handleCellClick = (entityName: string, cat: StrideCategory) => {
    setCategoryFilter(cat);
    setStatusFilter('all');
    setEntityFilter(entityName);
    setViewMode('list');
    // Ensure entity is expanded in list view
    setCollapsedEntities((prev) => {
      const next = new Set(prev);
      next.delete(entityName);
      return next;
    });
  };

  const clearEntityFilter = () => setEntityFilter('all');

  // ── Render ──────────────────────────────────────────────────────────────

  if (!projectId) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Connect to a ThreatCat project to view STRIDE analysis.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading && threats.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Loading STRIDE analysis...</p>
        </CardContent>
      </Card>
    );
  }

  if (threats.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Shield className="h-5 w-5" />
            <p>No STRIDE analysis yet.</p>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            Use the MCP tool <code className="bg-muted px-1.5 py-0.5 rounded text-xs">push_stride_threats</code> to push STRIDE analysis results.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* STRIDE Category Cards */}
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        {ALL_CATEGORIES.map((cat) => {
          const c = categoryCounts[cat];
          const isActive = categoryFilter === cat;
          return (
            <Card
              key={cat}
              className={`cursor-pointer transition-all ${isActive ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'}`}
              onClick={() => setCategoryFilter(isActive ? 'all' : cat)}
            >
              <CardHeader className="pb-1 pt-3 px-3">
                <div className="flex items-center justify-between">
                  <span className={`rounded px-1.5 py-0.5 text-xs font-bold ${STRIDE_COLORS[cat]}`}>
                    {cat}
                  </span>
                  <span className="text-lg font-bold">{c.total}</span>
                </div>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <p className="text-xs text-muted-foreground truncate">{STRIDE_LABELS[cat]}</p>
                {c.total > 0 && (
                  <div className="flex gap-1.5 mt-1.5">
                    {c.open > 0 && <span className="text-[10px] text-red-600 dark:text-red-400">{c.open} open</span>}
                    {c.partial > 0 && <span className="text-[10px] text-amber-600 dark:text-amber-400">{c.partial} partial</span>}
                    {c.mitigated > 0 && <span className="text-[10px] text-green-600 dark:text-green-400">{c.mitigated} ok</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Status Filter + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          {(['all', 'open', 'partial', 'mitigated', 'accepted', 'na'] as const).map((s) => {
            const count = statusCounts[s] || 0;
            const isActive = statusFilter === s;
            const label = s === 'all' ? 'All' : STATUS_CONFIG[s].label;
            return (
              <Button
                key={s}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7"
                onClick={() => setStatusFilter(isActive ? 'all' : s)}
              >
                {label} ({count})
              </Button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center rounded-md border">
            <Button
              variant={viewMode === 'heatmap' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 rounded-r-none"
              onClick={() => setViewMode('heatmap')}
              title="Heatmap view"
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 rounded-l-none"
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="destructive" size="sm" onClick={handleClearAll} disabled={clearing}>
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Clear All
          </Button>
        </div>
      </div>

      {/* Heatmap view */}
      {viewMode === 'heatmap' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              STRIDE Coverage Heatmap
              <span className="text-xs font-normal text-muted-foreground">
                ({heatmapData.length} components × 6 categories)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground pb-3 pr-4 min-w-[180px]">Component</th>
                    {ALL_CATEGORIES.map((cat) => (
                      <th key={cat} className="pb-3 px-1 text-center min-w-[56px]">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`h-2.5 w-2.5 rounded-full ${
                            cat === 'S' ? 'bg-purple-500' :
                            cat === 'T' ? 'bg-red-500' :
                            cat === 'R' ? 'bg-amber-500' :
                            cat === 'I' ? 'bg-blue-500' :
                            cat === 'D' ? 'bg-purple-400' :
                            'bg-rose-500'
                          }`} />
                          <span className="text-xs font-bold text-muted-foreground">{cat}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapData.map(({ name, counts }) => (
                    <tr key={name} className="group">
                      <td className="py-1.5 pr-4 text-sm font-medium truncate max-w-[240px]" title={name}>
                        {name}
                      </td>
                      {ALL_CATEGORIES.map((cat) => {
                        const count = counts[cat];
                        return (
                          <td key={cat} className="py-1.5 px-1 text-center">
                            <button
                              onClick={() => count > 0 && handleCellClick(name, cat)}
                              className={`inline-flex items-center justify-center w-11 h-8 rounded text-sm font-semibold transition-all ${
                                count === 0
                                  ? 'bg-muted/50 text-muted-foreground/40 cursor-default'
                                  : `cursor-pointer hover:ring-2 hover:ring-primary/50 ${
                                      cat === 'S' ? 'bg-purple-500/20 text-purple-300 dark:bg-purple-500/25 dark:text-purple-300' :
                                      cat === 'T' ? 'bg-red-500/20 text-red-300 dark:bg-red-500/25 dark:text-red-300' :
                                      cat === 'R' ? 'bg-amber-500/20 text-amber-300 dark:bg-amber-500/25 dark:text-amber-300' :
                                      cat === 'I' ? 'bg-blue-500/20 text-blue-300 dark:bg-blue-500/25 dark:text-blue-300' :
                                      cat === 'D' ? 'bg-purple-400/20 text-purple-300 dark:bg-purple-400/25 dark:text-purple-300' :
                                      'bg-rose-500/20 text-rose-300 dark:bg-rose-500/25 dark:text-rose-300'
                                    }`
                              }`}
                            >
                              {count}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <>
      {/* Info line + active entity chip */}
      <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
        <span>
          Showing {filtered.length} of {threats.length} threats
          {categoryFilter !== 'all' && ` · Category: ${STRIDE_LABELS[categoryFilter]}`}
          {statusFilter !== 'all' && ` · Status: ${STATUS_CONFIG[statusFilter].label}`}
        </span>
        {entityFilter !== 'all' && (
          <button
            onClick={clearEntityFilter}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            {entityFilter}
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Entity-grouped threats */}
      <div className="space-y-3">
        {Array.from(grouped.entries()).map(([entityName, entityThreats]) => {
          const first = entityThreats[0];
          const isCollapsed = collapsedEntities.has(entityName);
          const openCount = entityThreats.filter((t) => t.status === 'open').length;
          const partialCount = entityThreats.filter((t) => t.status === 'partial').length;

          return (
            <Collapsible key={entityName} open={!isCollapsed} onOpenChange={() => toggleEntity(entityName)}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        <CardTitle className="text-sm font-semibold">{entityName}</CardTitle>
                        {first.entity_type && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            {first.entity_type}
                          </span>
                        )}
                        {first.boundary && (
                          <span className="text-xs text-muted-foreground">
                            {first.boundary}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium">{entityThreats.length}</span>
                        {openCount > 0 && <span className="text-red-600 dark:text-red-400">{openCount} open</span>}
                        {partialCount > 0 && <span className="text-amber-600 dark:text-amber-400">{partialCount} partial</span>}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-3">
                    <div className="divide-y">
                      {entityThreats.map((t) => (
                        <div key={t.id} className="py-2.5 first:pt-0 last:pb-0">
                          <div className="flex items-start gap-2">
                            {/* STRIDE badge */}
                            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold mt-0.5 ${STRIDE_COLORS[t.stride_category]}`}>
                              {t.stride_category}
                            </span>

                            {/* Content */}
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {t.external_id && (
                                    <span className="font-mono text-xs text-muted-foreground shrink-0">{t.external_id}</span>
                                  )}
                                  <span className={`text-xs font-medium ${STATUS_CONFIG[t.status].color}`}>
                                    {STATUS_CONFIG[t.status].icon} {STATUS_CONFIG[t.status].label}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                                  onClick={() => handleDelete(t.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>

                              <p className="text-sm leading-relaxed">{t.threat}</p>

                              {t.mitigation && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Mitigation:</span> {t.mitigation}
                                </p>
                              )}

                              <div className="flex items-center gap-2 flex-wrap">
                                {t.finding_ref && (
                                  <span className="rounded bg-blue-100 dark:bg-blue-950 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-400">
                                    {t.finding_ref}
                                  </span>
                                )}
                                {t.asrg_ids?.split(',').map((id) => (
                                  <span
                                    key={id.trim()}
                                    className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                  >
                                    {id.trim()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>

        </>
      )}
    </div>
  );
}
